import { FC, useState } from "react";

import { TaskDriverStatusEnum } from "@/domain/models/common";
import { JunkLoadStatusEnum, JunkLoadType } from "@/domain/models/job";
import { i18n } from "@/i18n";

import { OptionType } from "../blocks";
import { useUpdateServiceMutation } from "../data/mutations/serviceMutations";
import { useUpdateTaskMutation } from "../data/mutations/taskMutations";
import { useServiceQuery } from "../data/queries/serviceQueries";
import { JobServiceTaskBaseType } from "../domain/task";
import { DriverScreenWithOptions } from "./DriverScreenWithOptions";

type ValueType = "YES" | "NO";

const LOCATION_ACCESS_OPTIONS: OptionType<ValueType>[] = [
  {
    label: i18n.t("yes"),
    value: "YES",
  },
  {
    label: i18n.t("no"),
    value: "NO",
  },
];

const DriverNeedAnotherLoadStep: FC<JobServiceTaskBaseType> = ({
  jobId,
  serviceId,
  taskId,
}) => {
  const [selectedNeedAnotherLoad, setSelectedNeedAnotherLoad] =
    useState<ValueType | null>(null);

  const { data: serviceData } = useServiceQuery({ jobId, serviceId });

  const {
    mutate: updateService,
    isPending: isUpdatingService,
    variables,
  } = useUpdateServiceMutation();
  const { mutate: updateTask, isPending: isUpdatingTask } =
    useUpdateTaskMutation();

  const onNextClick = () => {
    const willDriverNeedAnotherLoad = selectedNeedAnotherLoad === "YES";

    // If driver don't need another load and didn't make any previous load before, go to filling interior cleanup type
    if (!willDriverNeedAnotherLoad && !serviceData?.junkLoads?.length) {
      updateService({
        jobId,
        serviceId,
        driverFlowStatus: TaskDriverStatusEnum.FILLING_INTERIOR_CLEANUP_TYPE,
      });

      updateTask({
        jobId,
        serviceId,
        taskId,
        driverFlowStatus: TaskDriverStatusEnum.FILLING_INTERIOR_CLEANUP_TYPE,
      });

      return;
    }

    // Collect every step filled so far and mount the junk load object
    const junkLoad: JunkLoadType = {
      preJunkRemovalPhoto: serviceData?.preJunkRemovalPhoto,
      loadSize: serviceData?.loadSize,
      locationAccess: serviceData?.locationAccess,
      postJunkRemovalPhoto: serviceData?.postJunkRemovalPhoto,
      loadNumber: (serviceData?.junkLoads?.length ?? 0) + 1,
      status: willDriverNeedAnotherLoad
        ? JunkLoadStatusEnum.IN_PROGRESS
        : JunkLoadStatusEnum.COMPLETED,
    };

    const currentJunkLoads = serviceData?.junkLoads ?? [];

    updateService({
      jobId,
      serviceId,
      junkLoads: [...currentJunkLoads, junkLoad],
      // Clean up the fields that are not needed anymore from the base object of the service
      preJunkRemovalPhoto: null,
      loadSize: null,
      locationAccess: null,
      postJunkRemovalPhoto: null,
      driverFlowStatus: willDriverNeedAnotherLoad
        ? TaskDriverStatusEnum.JUNK_LOAD_REVIEW
        : TaskDriverStatusEnum.FILLING_INTERIOR_CLEANUP_TYPE,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus: willDriverNeedAnotherLoad
        ? TaskDriverStatusEnum.JUNK_LOAD_REVIEW
        : TaskDriverStatusEnum.FILLING_INTERIOR_CLEANUP_TYPE,
    });
  };

  const isLoading = isUpdatingService || isUpdatingTask;

  const isNextButtonLoading =
    isLoading &&
    (variables?.driverFlowStatus ===
      TaskDriverStatusEnum.FILLING_INTERIOR_CLEANUP_TYPE ||
      variables?.driverFlowStatus === TaskDriverStatusEnum.JUNK_LOAD_REVIEW);

  const isPreviousButtonLoading =
    isLoading &&
    variables?.driverFlowStatus ===
      TaskDriverStatusEnum.FILLING_LOCATION_ACCESS;

  const goToPreviousStep = () => {
    updateService({
      jobId,
      serviceId,
      driverFlowStatus: TaskDriverStatusEnum.FILLING_LOCATION_ACCESS,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus: TaskDriverStatusEnum.FILLING_LOCATION_ACCESS,
    });
  };

  return (
    <DriverScreenWithOptions
      jobId={jobId}
      serviceId={serviceId}
      taskId={taskId}
      title="driver_need_another_load.title"
      optionsSelectorProps={{
        options: LOCATION_ACCESS_OPTIONS,
        onSelect: setSelectedNeedAnotherLoad,
        value: selectedNeedAnotherLoad,
      }}
      bottomButtonProps={{
        isNextButtonLoading,
        onNextButtonClick: onNextClick,
        isNextButtonDisabled: !selectedNeedAnotherLoad,
        previousButtonTitle: "driver_need_another_load.back_button",
        onPreviousButtonClick: goToPreviousStep,
        isPreviousButtonLoading,
      }}
    />
  );
};

export { DriverNeedAnotherLoadStep };
